import axios from 'axios';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import env from './loadEnv';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import * as Sentry from '@sentry/react';

// Track network status for use in the app
let networkStatus = { connected: true };

// Set up network status listener
if (Capacitor.isPluginAvailable('Network')) {
  Network.addListener('networkStatusChange', status => {
    networkStatus.connected = status.connected;
  });

  // Initial network status check
  Network.getStatus().then(status => {
    networkStatus.connected = status.connected;
  });
}

export const api = axios.create({
  baseURL: env.VITE_PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Set default timeout for all requests
  timeout: 20000, // 20 seconds
});

// Check network before making requests
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  try {
    // Check for network connectivity first
    if (Capacitor.isPluginAvailable('Network')) {
      const status = await Network.getStatus();
      if (!status.connected) {
        console.warn('Network is offline. Request will likely fail:', config.url);
        Sentry.addBreadcrumb({
          category: 'network',
          message: `Attempting request while offline: ${config.url}`,
          level: 'warning'
        });

        // We still attempt the request in case the network status is wrong
        // The error will be caught in the response interceptor
      }
    }

    // Get current Firebase user and add token
    const token = (await FirebaseAuthentication.getIdToken()).token;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request to Sentry breadcrumbs
    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      level: 'info'
    });

    return config;
  } catch (error) {
    console.error('Error in request interceptor:', error);
    Sentry.captureException(error, {
      extra: { context: 'API request interceptor', url: config.url }
    });
    return Promise.reject(error);
  }
});

// Handle token expiry and network errors
api.interceptors.response.use(
  (response) => {
    // Add successful response to Sentry breadcrumbs
    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Response Success: ${response.config.method?.toUpperCase()} ${response.config.url}`,
      level: 'info'
    });
    return response;
  },
  async (error) => {
    // Handle network errors and token expiry
    if (error.isAxiosError) {
      const axiosError = error as AxiosError;
      const originalRequest = axiosError.config;

      if (!originalRequest) {
        console.error('No config in axios error');
        return Promise.reject(error);
      }

      // Add error to Sentry breadcrumbs
      Sentry.addBreadcrumb({
        category: 'api',
        message: `API Error: ${originalRequest.method?.toUpperCase()} ${originalRequest.url} - ${axiosError.code || 'Unknown error'}`,
        level: 'error'
      });

      // Handle network connectivity errors
      if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ECONNABORTED' || axiosError.message.includes('Network Error')) {
        console.error('Network error detected:', axiosError.message);

        // Check current network status
        if (Capacitor.isPluginAvailable('Network')) {
          const status = await Network.getStatus();
          if (!status.connected) {
            console.warn('Device is confirmed offline');
            Sentry.captureMessage('API request failed - device offline', {
              level: 'warning',
              extra: { url: originalRequest.url, method: originalRequest.method }
            });
          }
        }

        // Enhance error message for better user feedback
        const enhancedError = new AxiosError(
          'Connexion au serveur impossible. Vérifiez votre connexion internet.',
          axiosError.code,
          axiosError.config,
          axiosError.request,
          axiosError.response
        );
        return Promise.reject(enhancedError);
      }

      // Handle timeout errors
      if (axiosError.code === 'ETIMEDOUT' || axiosError.message.includes('timeout')) {
        console.error('Request timeout:', axiosError.message);
        Sentry.captureMessage('API request timeout', {
          level: 'warning',
          extra: { url: originalRequest.url, method: originalRequest.method }
        });

        // Enhance error message for better user feedback
        const enhancedError = new AxiosError(
          'La requête a pris trop de temps. Vérifiez votre connexion internet ou réessayez plus tard.',
          'ETIMEDOUT',
          axiosError.config,
          axiosError.request,
          axiosError.response
        );
        return Promise.reject(enhancedError);
      }

      // If error is 401 and we haven't retried yet, refresh token and retry
      if (axiosError.response?.status === 401 && !(originalRequest as any)._retry) {
        (originalRequest as any)._retry = true;

        try {
          // Get fresh token
          const token = (await FirebaseAuthentication.getIdToken()).token;

          if (token) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (tokenError) {
          console.error('Error refreshing token:', tokenError);
          Sentry.captureException(tokenError, {
            extra: { context: 'Token refresh in API interceptor' }
          });
        }
      }
    }

    return Promise.reject(error);
  }
);

// Export a function to check network status
const isNetworkAvailable = async (): Promise<boolean> => {
  if (Capacitor.isPluginAvailable('Network')) {
    const status = await Network.getStatus();
    return status.connected;
  }
  return navigator.onLine; // Fallback to browser API
};
